import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Package, MapPin, Wallet, Landmark } from "lucide-react";

interface ResolutionSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'replacement' | 'store_credit' | 'refund';
  amount?: string | number;
  productTitle?: string;
}

const ResolutionSuccessModal: React.FC<ResolutionSuccessModalProps> = ({
  isOpen,
  onClose,
  type,
  amount,
}) => {
  const getIcon = () => {
    switch (type) {
      case 'replacement': return <Package className="tw-w-10 tw-h-10 tw-text-[#00C5FB]" />;
      case 'store_credit': return <Wallet className="tw-w-10 tw-h-10 tw-text-[#00C5FB]" />;
      case 'refund': return <Landmark className="tw-w-10 tw-h-10 tw-text-[#00C5FB]" />;
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'replacement': return "Replacement Started";
      case 'store_credit': return "Refund Started";
      case 'refund': return "Refund Started";
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'replacement': return "Your replacement has been successfully initiated. Our logistics partner will contact you shortly for pickup.";
      case 'store_credit': return `₹${amount} is being processed to your Protega Wallet. Please allow 24-48 hours after pickup for it to reflect.`;
      case 'refund': return `₹${amount} is being processed to your original payment method. Please allow 3-5 business days for it to reflect.`;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[150] tw-flex tw-items-center tw-justify-center tw-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="tw-absolute tw-inset-0 tw-bg-black/95 tw-backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 30 }}
            className="tw-relative tw-bg-[#0d0d0d] tw-rounded-[32px] tw-w-full tw-max-w-sm tw-overflow-hidden tw-shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] tw-border tw-border-white/10 tw-p-8 sm:tw-p-10 tw-text-center"
          >
            {/* Success Icon Group */}
            <div className="tw-relative tw-w-24 tw-h-24 tw-mx-auto tw-mb-8">
              <div className="tw-absolute tw-inset-0 tw-bg-[#112126] tw-rounded-[28px] tw-border tw-border-white/5" />
              <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                {getIcon()}
              </div>
              <div className="tw-absolute tw--bottom-1 tw--right-1 tw-w-7 tw-h-7 tw-bg-[#00C5FB] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-4 tw-border-[#0d0d0d] tw-shadow-lg">
                <Check className="tw-w-3 tw-h-3 tw-text-black" strokeWidth={5} />
              </div>
            </div>

            <h2 className="tw-text-white tw-font-bold tw-text-3xl tw-mb-4 tw-tracking-tight">{getTitle()}</h2>
            <p className="tw-text-[#BBBBBB] tw-text-sm tw-mb-10 tw-leading-relaxed tw-px-2 tw-font-medium">
              {getDescription()}
            </p>

            {/* Next Steps for All Types */}
            <div className="tw-bg-[#161616] tw-rounded-2xl tw-p-5 tw-mb-8 tw-text-left tw-border tw-border-white/5">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                <MapPin className="tw-w-4 tw-h-4 tw-text-[#00C5FB]" />
                <span className="tw-text-[10px] tw-font-black tw-text-white tw-uppercase tw-tracking-[0.1em]">Next Step</span>
              </div>
              <p className="tw-text-[11px] tw-text-gray-300 tw-leading-relaxed">
                {type === 'replacement' 
                  ? "Prepare the original item in its packaging. A representative will arrive within 24-48 hours for pickup and delivery." 
                  : "Prepare the item in its packaging. Once the pickup is verified, your refund will be processed immediately."}
              </p>
            </div>

            <button 
              onClick={onClose}
              className="tw-w-full tw-py-4 tw-rounded-2xl tw-bg-[#00C5FB] tw-text-black tw-font-black tw-text-sm tw-border-none tw-cursor-pointer hover:tw-bg-cyan-400 tw-transition-all tw-shadow-[0_8px_20px_-4px_rgba(0,197,251,0.4)]"
            >
              Back to Orders
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ResolutionSuccessModal;
